'use client';

import React, { Suspense, useRef } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  registerUser,
  googleLogin,
  getInvitationDetails,
} from '../../../lib/slices/authSlice';
import { AppDispatch } from '../../../lib/store';
import GoogleLoginButton from '@/components/GoogleLoginButton';
import Cookies from 'js-cookie';
import { useSearchParams } from 'next/navigation';
import { Logo } from '@/components/ui/Logo';
import Loader from '@/components/ui/Loader';

const RegisterSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]{8,}$/,
      'Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Required'),
});



 function RegisterContent() {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const referralCode = searchParams?.get('ref');

  useEffect(() => {
    const token = searchParams?.get('token');
    if (token) {
      dispatch(getInvitationDetails(token))
        .unwrap()
        .then((result) => {
          if (result.success) {
            setInvitationDetails(result.invitation);
            // Use Formik's setFieldValue to set the email
            if (formikRef.current) {
              formikRef.current.setFieldValue('email', result.invitation.email);
            }
          } else {
            toast.error('Invalid or expired invitation');
          }
        })
        .catch((error) => {
          console.error('Error fetching invitation details:', error);
          toast.error('Failed to fetch invitation details');
        });
    }
  }, [dispatch, searchParams]);

  // Add this ref to get access to Formik methods
  const formikRef = useRef<any>(null);

  const session = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (
    values: {
      name: string;
      email: string;
      password: string;
      confirmPassword: string;
    },
    actions?: any
  ) => {
    setIsLoading(true);
    try {
      const { name, email, password } = values;
      const result = await dispatch(
        registerUser({
          name,
          email,
          password,
          auth_provider: 'app',
          invitation_token: searchParams?.get('token') || undefined,
          referral_code: referralCode || undefined,
        })
      );

      if (registerUser.fulfilled.match(result)) {
        if (result?.payload?.status === 403) {
          const errorMessage:any = result?.payload?.message || 'Access forbidden. Please try again.';
          toast.error(
            errorMessage
          );
          await new Promise((resolve) => setTimeout(resolve, 2000));
        } else {
          toast.success(
            result.payload.message.registration +
              ' ' +
              result.payload.message.more_information
          );
        }
        router.push('/enter-code?token=' + result.payload.hash_url);
      } else if (registerUser.rejected.match(result)) {
        if (
          result.payload &&
          typeof result.payload === 'object' &&
          'error' in result.payload
        ) {
          const errorPayload = result.payload as {
            error: { [key: string]: string[] };
          };
          Object.entries(errorPayload.error).forEach(([field, messages]) => {
            messages.forEach((message) => toast.error(`${field}: ${message}`));
          });
        } else {
          toast.error('Registration failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isButtonClicked = localStorage.getItem('google_login_button_clicked');
    const token = session.data?.accessToken;
    if (isButtonClicked && token) {
      const handleGoogleLogin = async () => {
        const response = await dispatch(
          googleLogin({
            token,
            auth_provider: 'google',
            invitation_token: searchParams?.get('token') || undefined,
          })
        );
        if (googleLogin.fulfilled.match(response)) {
          // Set cookies here
          Cookies.set('accessToken', response.payload.token, {
            expires: 30,
            secure: process.env.NODE_ENV === 'production',
            sameSite: process.env.NODE_ENV === 'production' ? 'strict' : 'lax',
            path: '/',
          });
          toast.success('Google Authentication successful');
          router.push('/dashboard');
          // Add page refresh
        } else if (googleLogin.rejected.match(response)) {
          const errorPayload = response.payload as {
            success: boolean;
            errors: string;
          };
          if (
            errorPayload &&
            typeof errorPayload === 'object' &&
            'errors' in errorPayload
          ) {
            toast.error(errorPayload.errors);
          } else {
            toast.error('Failed to complete Google login');
          }
        }
      };
      handleGoogleLogin();
      localStorage.removeItem('google_login_button_clicked');
    }
  }, [session, dispatch, router]);

  const handleGoogleLogin = async () => {
    localStorage.setItem('google_login_button_clicked', 'true');
    try {
      await signIn('google', { redirect: true });
    } catch (error) {
      console.error('Google sign-in error:', error);
      toast.error('Failed to sign in with Google');
      localStorage.removeItem('google_login_button_clicked');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <Toaster />
      <div className="text-center mb-8">
        <Logo className="w-32 h-32 mx-auto" showText={false} />
        <span className="block text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mt-1">
          AINativeForms
        </span>
      </div>

      <div className="max-w-md w-full space-y-8 p-10 bg-white rounded-xl shadow-lg relative z-10">
        <div className="text-center">
          <h2 className="text-2xl font-extrabold text-gray-900">
            {invitationDetails
              ? `Join ${invitationDetails.organization.name}`
              : 'Create your account'}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {invitationDetails
              ? "You're just a few steps away from joining your team"
              : 'Start creating intelligent forms powered by AI'}
          </p>
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={{
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
          }}
          validationSchema={RegisterSchema}
          onSubmit={(values, actions) => handleSubmit(values, actions)}
          enableReinitialize
        >
          {({ isSubmitting, handleSubmit }) => (
            <Form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Field
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    className="mt-1 block w-full rounded-md"
                    as={Input}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email address</Label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="mt-1 block w-full rounded-md"
                    as={Input}
                    disabled={!!invitationDetails}
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Field
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="mt-1 block w-full rounded-md"
                    as={Input}
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Field
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="mt-1 block w-full rounded-md"
                    as={Input}
                  />
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-500 hover:bg-purple-600 text-white"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? 'Registering...' : 'Register'}
              </Button>
            </Form>
          )}
        </Formik>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                Or continue with
              </span>
            </div>
          </div>
          <div className="mt-6">
            <GoogleLoginButton onClick={handleGoogleLogin} />
          </div>
        </div>
        <div className="text-center">
          <p className="mt-2 text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              href="/login"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}


export default function RegisterPage() {
  return (
    <Suspense fallback={<Loader />}>
      <RegisterContent />
    </Suspense>
  );
}
